import moment from 'moment';
import type { IEventItem } from '@op/components/ChartsEvents/types';
import type { IEventClusterItem } from '@/pages/intelligence/services/eventCluster';
import { resolveOpinionLineChartEventTypeKey } from '../../utils/eventTypeStyle';
import type { IOpinionChartEventData } from '../types';
import { getTimelineTimeFormat } from './getTimelineTimeFormat';

function mapEventTypeCode(eventTypeCode?: string, eventType?: string): string {
  const raw = (eventTypeCode || eventType || '').trim();
  return resolveOpinionLineChartEventTypeKey(raw);
}

/** 解析后端 reference_urls 可能存在的非标准 JSON 拼接格式 */
export function parseEventClusterReferenceUrls(
  referenceUrls?: string[],
): string[] {
  if (!Array.isArray(referenceUrls) || !referenceUrls.length) {
    return [];
  }

  const joined = referenceUrls.join('');
  try {
    const parsed = JSON.parse(joined);
    if (Array.isArray(parsed)) {
      return parsed.filter(Boolean).map(String);
    }
  } catch {
    // fall through to regex extraction
  }

  return referenceUrls
    .flatMap(item => {
      const matches = String(item).match(/https?:\/\/[^\s"'\\]+/g);
      return matches || [];
    })
    .filter(Boolean);
}

export function mapEventClusterItemToEventItem(
  item: IEventClusterItem,
): IEventItem {
  const referenceUrls = parseEventClusterReferenceUrls(item.reference_urls);
  const displayEventType = mapEventTypeCode(
    item.event_type_code,
    item.event_type,
  );

  return {
    name: item.canonical_title_en,
    name_cn: item.canonical_title_cn,
    description: item.canonical_description_en,
    description_cn: item.canonical_description_cn,
    event_type: displayEventType as IEventItem['event_type'],
    event_type_cn: item.event_type_cn,
    start_time: item.event_date_start,
    end_time: item.event_date_end,
    url: referenceUrls[0],
    cluster_id: item.cluster_id,
    cover_image_urls: item.cover_image_urls,
    reference_urls: referenceUrls,
    major_event: item.major_event,
    major_event_cn: item.major_event_cn,
    countries: item.countries,
    country: item.countries?.length === 1 ? item.countries[0] : undefined,
  };
}

function appendEventToBucket(
  bucket: Record<string, IEventItem[]>,
  event: IEventItem,
) {
  const typeKey = event.event_type || 'Others';
  if (!bucket[typeKey]) {
    bucket[typeKey] = [];
  }
  bucket[typeKey].push(event);
}

export function mapEventClusterListToOpinionEventData(
  list: IEventClusterItem[] = [],
  dateType?: string,
): IOpinionChartEventData {
  const format = getTimelineTimeFormat(dateType);
  const eventList: Record<string, IEventItem[]> = {};
  const chartRenderEventList: IOpinionChartEventData['chartRenderEventList'] =
    {};

  list.forEach(rawItem => {
    if (
      rawItem?.event_date_start === null ||
      rawItem?.event_date_start === undefined
    ) {
      return;
    }

    const event = mapEventClusterItemToEventItem(rawItem);
    appendEventToBucket(eventList, event);

    const m = moment(rawItem.event_date_start);
    const timeKey = m.isValid()
      ? m.format(format)
      : String(rawItem.event_date_start);

    if (!chartRenderEventList[timeKey]) {
      chartRenderEventList[timeKey] = {
        count: 0,
        event_list: {},
      };
    }

    chartRenderEventList[timeKey].count += 1;
    appendEventToBucket(chartRenderEventList[timeKey].event_list, event);
  });

  return {
    eventList,
    chartRenderEventList,
    format,
    isEmpty: Object.keys(chartRenderEventList).length === 0,
  };
}
